import { useState, useEffect, useCallback } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  CheckSquare, 
  LayoutDashboard, 
  ListTodo, 
  Heart,
  LogOut, 
  CheckCircle2,
  Circle,
  FolderOpen,
  Filter,
  Star,
  StarOff,
  HandHelping,
  Search,
  X
} from "lucide-react";

const EmployeeDashboard = () => {
  const { user, logout, api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [tasks, setTasks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assets, setAssets] = useState([]);
  const [helpingTasks, setHelpingTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const apiCalls = [
        api.get("/tasks"),
        api.get("/categories"),
        api.get("/assets"),
      ];
      
      // Fetch tasks we're helping with if user is an asset
      if (user?.is_asset) {
        apiCalls.push(api.get("/tasks/helping"));
      }
      
      const responses = await Promise.all(apiCalls);
      setTasks(responses[0].data);
      setCategories(responses[1].data);
      setAssets(responses[2].data);
      if (user?.is_asset && responses[3]) {
        setHelpingTasks(responses[3].data);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  }, [api, user?.is_asset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success("Logged out successfully");
  };

  // Build navigation items dynamically based on user's asset status
  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Overview", exact: true },
    { path: "/dashboard/tasks", icon: ListTodo, label: "Browse Responsibilities" },
    { path: "/dashboard/my-tasks", icon: CheckCircle2, label: "My Responsibilities" },
  ];
  
  // Add "Helping With" nav item if user is an asset
  if (user?.is_asset) {
    navItems.push({ path: "/dashboard/helping", icon: HandHelping, label: "Helping With" });
  }

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path) && path !== "/dashboard";
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="sidebar w-64 bg-white border-r border-border/50 flex flex-col" data-testid="employee-sidebar">
        <div className="p-6">
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer" data-testid="logo-home-link">
            <img src="/images/logo.png" alt="Future Owners Logo" className="w-12 h-12 object-contain" />
            <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>Future Owners</span>
          </Link>
        </div>
        
        <nav className="flex-1 px-4">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all duration-200 ${
                isActive(item.path, item.exact) || (item.exact && location.pathname === item.path)
                  ? "bg-primary text-white"
                  : "text-muted-foreground hover:bg-secondary hover:text-primary"
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-border/50">
          <div className="flex items-center gap-3 px-4 py-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-primary font-semibold">{user?.name?.[0]?.toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground">Future Owner</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <Routes>
            <Route index element={
              <EmployeeOverview 
                tasks={tasks} 
                userId={user?.id}
                helpingTasksCount={helpingTasks.length}
                isAsset={user?.is_asset}
              />
            } />
            <Route path="tasks" element={
              <BrowseTasks 
                tasks={tasks} 
                categories={categories}
                userId={user?.id}
                onInterestChange={fetchData}
                api={api}
              />
            } />
            <Route path="my-tasks" element={
              <MyTasks 
                tasks={tasks} 
                userId={user?.id}
                assets={assets}
                api={api}
                navigate={navigate}
              />
            } />
            {user?.is_asset && (
              <Route path="helping" element={
                <HelpingWith 
                  helpingTasks={helpingTasks}
                  assets={assets}
                  api={api}
                  navigate={navigate}
                />
              } />
            )}
          </Routes>
        )}
      </main>
    </div>
  );
};

// Employee Overview Component
const EmployeeOverview = ({ tasks, userId, helpingTasksCount, isAsset }) => {
  const myInterested = tasks.filter(t => t.interested_employees.includes(userId) && t.status === "open");
  const myAssigned = tasks.filter(t => t.assigned_to === userId);
  const openTasks = tasks.filter(t => t.status === "open");

  const stats = [
    { label: "Available Responsibilities", value: openTasks.length, icon: ListTodo, color: "bg-primary/10 text-primary" },
    { label: "My Interests", value: myInterested.length, icon: Heart, color: "bg-pink-100 text-pink-600" },
    { label: "Assigned to Me", value: myAssigned.length, icon: CheckCircle2, color: "bg-green-100 text-green-600" },
  ];
  
  // Add "Helping With" stat if user is an asset
  if (isAsset) {
    stats.push({ label: "Helping With", value: helpingTasksCount || 0, icon: HandHelping, color: "bg-orange-100 text-orange-600" });
  }

  return (
    <div className="animate-in" data-testid="employee-overview-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Welcome Back!
        </h1>
        <p className="text-muted-foreground">Here's your responsibility summary for today.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {stats.map((stat, index) => (
          <Card key={index} className="border-border/50 shadow-soft hover-lift" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
            <CardContent className="p-6">
              <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center mb-4`}>
                <stat.icon className="w-6 h-6" />
              </div>
              <p className="text-3xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</p>
              <p className="text-sm text-muted-foreground">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tasks I'm Interested In */}
        <Card className="border-border/50 shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Heart className="w-5 h-5 text-pink-500" />
              My Interests
            </CardTitle>
            <CardDescription>Responsibilities you've expressed interest in</CardDescription>
          </CardHeader>
          <CardContent>
            {myInterested.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">You haven't expressed interest in any responsibilities yet.</p>
            ) : (
              <div className="space-y-3">
                {myInterested.slice(0, 5).map((task) => (
                  <div key={task.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                    <div className="flex items-center gap-3">
                      <Star className="w-5 h-5 text-primary fill-primary" />
                      <span className="font-medium">{task.title}</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">{task.category}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Assigned Tasks */}
        <Card className="border-border/50 shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Assigned to Me
            </CardTitle>
            <CardDescription>Responsibilities you're responsible for</CardDescription>
          </CardHeader>
          <CardContent>
            {myAssigned.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No responsibilities assigned to you yet.</p>
            ) : (
              <div className="space-y-3">
                {myAssigned.slice(0, 5).map((task) => (
                  <div key={task.id} className="flex items-center justify-between p-3 rounded-lg bg-green-50">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      <span className="font-medium">{task.title}</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">{task.category}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Browse Tasks Component
const BrowseTasks = ({ tasks, categories, userId, onInterestChange, api }) => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isUpdating, setIsUpdating] = useState(null);
  const navigate = useNavigate();

  const openTasks = tasks.filter(t => t.status === "open");
  
  // Apply all filters
  let filteredTasks = openTasks.filter(task => {
    // Search filter
    const matchesSearch = searchQuery === "" || 
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (task.description && task.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      task.category.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Category filter
    const matchesCategory = selectedCategory === "all" || task.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });
  
  // Apply interest status filter
  if (selectedStatus === "interested") {
    filteredTasks = filteredTasks.filter(t => t.interested_employees.includes(userId));
  } else if (selectedStatus === "not-interested") {
    filteredTasks = filteredTasks.filter(t => !t.interested_employees.includes(userId));
  }

  const handleToggleInterest = async (taskId, isCurrentlyInterested) => {
    setIsUpdating(taskId);
    try {
      if (isCurrentlyInterested) {
        await api.delete(`/tasks/${taskId}/interest`);
        toast.success("Interest removed");
      } else {
        await api.post(`/tasks/${taskId}/interest`);
        toast.success("Interest expressed!");
      }
      onInterestChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update interest");
    } finally {
      setIsUpdating(null);
    }
  };

  return (
    <div className="animate-in" data-testid="browse-tasks-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Browse Responsibilities
          </h1>
          <p className="text-muted-foreground">Explore available responsibilities and express your interest.</p>
        </div>
      </div>

      {/* Search and Filters Row */}
      <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-secondary/30 rounded-lg">
        {/* Search Input */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search responsibilities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-8"
            data-testid="search-input"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* Category Filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Category:</span>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[140px] h-9" data-testid="category-filter">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Interest Status Filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Interest:</span>
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-[150px] h-9" data-testid="status-filter">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="interested">I'm Interested</SelectItem>
              <SelectItem value="not-interested">Not Interested Yet</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Results count */}
        <div className="ml-auto">
          <Badge variant="outline" className="text-muted-foreground">
            {filteredTasks.length} of {openTasks.length} {openTasks.length === 1 ? 'responsibility' : 'responsibilities'}
          </Badge>
        </div>
      </div>

      {filteredTasks.length === 0 ? (
        <Card className="border-border/50 shadow-soft">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
              <Search className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {openTasks.length === 0 ? "No Responsibilities Available" : "No Results Found"}
            </h3>
            <p className="text-muted-foreground text-center mb-4">
              {openTasks.length === 0 
                ? "Check back later for new responsibilities."
                : "Try adjusting your search or filters."}
            </p>
            {(searchQuery || selectedCategory !== "all" || selectedStatus !== "all") && (
              <Button 
                variant="outline" 
                onClick={() => { setSearchQuery(""); setSelectedCategory("all"); setSelectedStatus("all"); }}
                className="rounded-full"
              >
                Clear Filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTasks.map((task) => {
            const isInterested = task.interested_employees.includes(userId);
            return (
              <Card 
                key={task.id} 
                className={`task-card border-border/50 shadow-soft hover-lift transition-all cursor-pointer hover:border-primary/50 ${
                  isInterested ? "border-l-4 border-l-primary" : ""
                }`}
                data-testid={`browse-task-${task.id}`}
                onClick={() => navigate(`/responsibility/${task.id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <Badge variant="secondary" className="text-xs">{task.category}</Badge>
                    {isInterested && (
                      <Badge className="bg-primary/10 text-primary text-xs">Interested</Badge>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {task.title}
                  </h3>
                  {task.description && (
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{task.description}</p>
                  )}
                  <div className="flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                    <span className="text-sm text-muted-foreground">
                      {task.interested_employees.length} interested
                    </span>
                    <Button
                      variant={isInterested ? "default" : "outline"}
                      size="sm"
                      className={`rounded-full transition-all ${
                        isInterested 
                          ? "bg-primary hover:bg-primary/90" 
                          : "hover:bg-primary/10 hover:text-primary hover:border-primary"
                      }`}
                      onClick={(e) => { e.stopPropagation(); handleToggleInterest(task.id, isInterested); }}
                      disabled={isUpdating === task.id}
                      data-testid={`interest-btn-${task.id}`}
                    >
                      {isUpdating === task.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                      ) : isInterested ? (
                        <>
                          <Star className="w-4 h-4 mr-2 fill-current" />
                          Interested
                        </>
                      ) : (
                        <>
                          <StarOff className="w-4 h-4 mr-2" />
                          Express Interest
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// My Tasks Component
const MyTasks = ({ tasks, userId, assets, api, navigate }) => {
  const myInterested = tasks.filter(t => t.interested_employees.includes(userId) && t.status === "open");
  const myAssigned = tasks.filter(t => t.assigned_to === userId);

  const getAssetInfo = (assetId) => {
    return assets.find(a => a.id === assetId);
  };

  return (
    <div className="animate-in" data-testid="my-tasks-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          My Tasks
        </h1>
        <p className="text-muted-foreground">View tasks you're interested in and assigned to.</p>
      </div>

      <div className="space-y-8">
        {/* Assigned Tasks */}
        <Card className="border-border/50 shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Assigned to Me ({myAssigned.length})
            </CardTitle>
            <CardDescription>Responsibilities you're responsible for completing</CardDescription>
          </CardHeader>
          <CardContent>
            {myAssigned.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No responsibilities assigned to you yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myAssigned.map((task) => (
                  <div 
                    key={task.id} 
                    className="p-4 rounded-lg bg-green-50 border border-green-200 cursor-pointer hover:border-green-400 transition-colors" 
                    data-testid={`my-assigned-${task.id}`}
                    onClick={() => navigate(`/responsibility/${task.id}`)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="secondary" className="text-xs">{task.category}</Badge>
                      <Badge className="bg-green-100 text-green-700 text-xs">Assigned</Badge>
                    </div>
                    <h4 className="font-semibold mb-2">{task.title}</h4>
                    {task.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{task.description}</p>
                    )}
                    
                    {/* Show assigned helpers */}
                    {task.assigned_assets && task.assigned_assets.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-green-200" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-green-700 flex items-center gap-1">
                            <HandHelping className="w-3 h-3" /> Your Helpers ({task.assigned_assets.length})
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {task.assigned_assets.map(assetId => {
                            const asset = getAssetInfo(assetId);
                            if (!asset) return null;
                            return (
                              <div 
                                key={assetId}
                                className="flex items-center gap-1 px-2 py-1 rounded-full bg-orange-100 text-orange-700 text-xs"
                                title={asset.email}
                              >
                                <HandHelping className="w-3 h-3" />
                                <span>{asset.name}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Interested Tasks */}
        <Card className="border-border/50 shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Heart className="w-5 h-5 text-pink-500" />
              My Interests ({myInterested.length})
            </CardTitle>
            <CardDescription>Responsibilities you've expressed interest in (pending assignment)</CardDescription>
          </CardHeader>
          <CardContent>
            {myInterested.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">You haven't expressed interest in any open responsibilities.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myInterested.map((task) => (
                  <div key={task.id} className="p-4 rounded-lg bg-pink-50 border border-pink-200" data-testid={`my-interest-${task.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="secondary" className="text-xs">{task.category}</Badge>
                      <Star className="w-4 h-4 text-primary fill-primary" />
                    </div>
                    <h4 className="font-semibold">{task.title}</h4>
                    <p className="text-xs text-muted-foreground mt-2">
                      {task.interested_employees.length} total interested
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Helping With Component - Tasks the user is helping with as an asset
const HelpingWith = ({ helpingTasks, assets, api, navigate }) => {
  const getAssetInfo = (assetId) => {
    return assets.find(a => a.id === assetId);
  };

  return (
    <div className="animate-in" data-testid="helping-with-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Helping With
        </h1>
        <p className="text-muted-foreground">Responsibilities you've been assigned to help with as an asset.</p>
      </div>

      {helpingTasks.length === 0 ? (
        <Card className="border-border/50 shadow-soft">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
              <HandHelping className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Not Helping With Any Tasks Yet</h3>
            <p className="text-muted-foreground text-center">When you're assigned to help with responsibilities, they'll appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {helpingTasks.map((task) => (
            <Card 
              key={task.id} 
              className="border-border/50 shadow-soft hover-lift cursor-pointer hover:border-orange-300 transition-colors" 
              data-testid={`helping-task-${task.id}`}
              onClick={() => navigate(`/responsibility/${task.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="secondary">{task.category}</Badge>
                  <div className="flex items-center gap-1">
                    <HandHelping className="w-4 h-4 text-orange-500" />
                    <span className="text-xs text-orange-600 font-medium">Helping</span>
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{task.title}</h3>
                {task.description && (
                  <p className="text-sm text-muted-foreground mb-4">{task.description}</p>
                )}
                
                {/* Show other helpers if any */}
                {task.assigned_assets && task.assigned_assets.length > 1 && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Other Helpers</p>
                    <div className="flex flex-wrap gap-2">
                      {task.assigned_assets.map(assetId => {
                        const asset = getAssetInfo(assetId);
                        if (!asset) return null;
                        return (
                          <div 
                            key={assetId}
                            className="flex items-center gap-1 px-2 py-1 rounded-full bg-orange-50 text-orange-700 text-xs"
                          >
                            <HandHelping className="w-3 h-3" />
                            <span>{asset.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmployeeDashboard;
